// Първи вход на това устройство: „Защитете приложението“.
// Стъпка 1 — 4-цифрен ПИН (два пъти). Стъпка 2 — предложение за лицево
// разпознаване/отпечатък, ако телефонът поддържа. ПИН-ът е резервният
// вход на заключения екран; може и да се пропусне изцяло.

import React, { useEffect, useState } from 'react';
import { Text, View } from 'react-native';
import * as LocalAuthentication from 'expo-local-authentication';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { BIOMETRIC_KEY, useAuth } from '../AuthContext';
import { PinDots, PinPad, usePinShake } from '../pin-pad';
import { setPin } from '../security';
import { showError, showSuccess } from '../toast';
import { Button, Card, COLORS, Constrain, Label, Muted, Screen, Title } from '../ui';
import { t } from '../i18n';

export default function SecuritySetupScreen() {
  const { completeSecuritySetup } = useAuth();
  const [step, setStep] = useState<'pin' | 'bio'>('pin');
  // Двете фази на ПИН-а: избор → повторение, с една и съща клавиатура
  const [phase, setPhase] = useState<'first' | 'repeat'>('first');
  const [first, setFirst] = useState('');
  const [entry, setEntry] = useState('');
  const [bioSupported, setBioSupported] = useState(false);
  const { offset, shake } = usePinShake();

  useEffect(() => {
    (async () => {
      const hardware = await LocalAuthentication.hasHardwareAsync();
      const enrolled = await LocalAuthentication.isEnrolledAsync();
      setBioSupported(hardware && enrolled);
    })();
  }, []);

  // Автоматично при 4-тата цифра: първата фаза запомня, втората сверява
  const onPinChange = async (value: string) => {
    setEntry(value);
    if (value.length !== 4) return;
    if (phase === 'first') {
      setFirst(value);
      setEntry('');
      setPhase('repeat');
      return;
    }
    if (value !== first) {
      shake();
      showError(t('Двата ПИН-а не съвпадат — започнете отначало.'));
      setEntry('');
      setFirst('');
      setPhase('first');
      return;
    }
    await setPin(value);
    if (bioSupported) {
      setStep('bio');
    } else {
      showSuccess(t('Готово — приложението се отключва с ПИН.'));
      await completeSecuritySetup();
    }
  };

  const enableBio = async () => {
    const result = await LocalAuthentication.authenticateAsync({ promptMessage: t('Потвърдете, за да включите') });
    if (!result.success) return;
    await AsyncStorage.setItem(BIOMETRIC_KEY, '1');
    showSuccess(t('Готово — отключвате с лице/отпечатък, а ПИН-ът е резервен.'));
    await completeSecuritySetup();
  };

  return (
    <Screen>
      <Constrain>
        <Title>{t('Защитете приложението')}</Title>
        <Muted>
          {t('В Livupp има лични данни и плащания. Изберете как да се отключва на този телефон — отнема половин минута.')}
        </Muted>

        {step === 'pin' && (
          <Card>
            <Label>{phase === 'first' ? t('1. Изберете 4-цифрен ПИН') : t('Повторете ПИН-а')}</Label>
            <PinDots value={entry} offset={offset} />
            <PinPad value={entry} onChange={onPinChange} />
            <Muted style={{ textAlign: 'center', marginTop: 14 }}>
              {t('ПИН-ът важи само за този телефон. Не е паролата на акаунта ви.')}
            </Muted>
            <Button
              title={t('Пропусни — без заключване')}
              variant="ghost"
              onPress={() => completeSecuritySetup()}
            />
          </Card>
        )}

        {step === 'bio' && (
          <Card>
            <Label>{t('2. Лицево разпознаване / отпечатък')}</Label>
            <Text style={{ fontSize: 44, textAlign: 'center', marginVertical: 8 }}>🔐</Text>
            <Muted>
              {t('Отключвайте с поглед или пръст — по-бързо е от ПИН. ПИН-ът остава като резервен вариант.')}
            </Muted>
            <Button title={t('Включи')} onPress={enableBio} />
            <Button
              title={t('Само с ПИН')}
              variant="secondary"
              onPress={async () => {
                showSuccess(t('Готово — приложението се отключва с ПИН.'));
                await completeSecuritySetup();
              }}
            />
          </Card>
        )}
      </Constrain>
    </Screen>
  );
}
